import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

interface LoanSettings {
  id?: string;
  group_id: string;
  interest_rate: number;
  min_contribution_amount: number;
  max_loan_to_contribution_ratio: number;
  max_repayment_months: number;
}

interface LoanSettingsCardProps {
  groupId: string;
  accessLevel?: string;
}

const LoanSettingsCard = ({ groupId, accessLevel }: LoanSettingsCardProps) => {
  const { userProfile } = useAuth();
  const isSuperAdmin = userProfile?.role === 'super_admin';
  const isGroupAdmin = userProfile?.group_roles?.some((r: any) => r === 'admin' || r === 'clerk');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<LoanSettings>({
    group_id: groupId,
    interest_rate: 10.0,
    min_contribution_amount: 0,
    max_loan_to_contribution_ratio: 0.5,
    max_repayment_months: 12,
  });
  const [formData, setFormData] = useState<LoanSettings>(settings);

  useEffect(() => {
    if (groupId) {
      fetchLoanSettings();
    }
  }, [groupId]);

  const fetchLoanSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('loan_settings')
        .select('*')
        .eq('group_id', groupId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (data) {
        setSettings({
          id: data.id,
          group_id: data.group_id,
          interest_rate: Number(data.interest_rate) || 10.0,
          min_contribution_amount: Number(data.min_contribution_amount) || 0,
          max_loan_to_contribution_ratio: Number(data.max_loan_to_contribution_ratio) || 0.5,
          max_repayment_months: Number(data.max_repayment_months) || 12,
        });
        setFormData({
          id: data.id,
          group_id: data.group_id,
          interest_rate: Number(data.interest_rate) || 10.0,
          min_contribution_amount: Number(data.min_contribution_amount) || 0,
          max_loan_to_contribution_ratio: Number(data.max_loan_to_contribution_ratio) || 0.5,
          max_repayment_months: Number(data.max_repayment_months) || 12,
        });
      } else {
        // No settings found, use defaults
        const defaultSettings: LoanSettings = {
          group_id: groupId,
          interest_rate: 10.0,
          min_contribution_amount: 0,
          max_loan_to_contribution_ratio: 0.5,
          max_repayment_months: 12,
        };
        setSettings(defaultSettings);
        setFormData(defaultSettings);
      }
    } catch (err: any) {
      console.error('Error fetching loan settings:', err);
      setError(err.message || 'Failed to load loan settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate form data
      if (formData.interest_rate < 0 || formData.interest_rate > 100) {
        throw new Error('Interest rate must be between 0 and 100');
      }
      if (formData.min_contribution_amount < 0) {
        throw new Error('Minimum contribution amount cannot be negative');
      }
      if (formData.max_loan_to_contribution_ratio <= 0 || formData.max_loan_to_contribution_ratio > 1) {
        throw new Error('Max loan to contribution ratio must be between 0 and 1');
      }
      if (formData.max_repayment_months <= 0) {
        throw new Error('Max repayment months must be greater than 0');
      }

      const settingsData = {
        group_id: groupId,
        interest_rate: formData.interest_rate,
        min_contribution_amount: formData.min_contribution_amount,
        max_loan_to_contribution_ratio: formData.max_loan_to_contribution_ratio,
        max_repayment_months: formData.max_repayment_months,
        updated_at: new Date().toISOString(),
      };

      if (settings.id) {
        // Update existing settings
        const { error: updateError } = await supabase
          .from('loan_settings')
          .update(settingsData)
          .eq('id', settings.id);

        if (updateError) throw updateError;
      } else {
        // Create new settings
        const { data: insertData, error: insertError } = await supabase
          .from('loan_settings')
          .insert(settingsData)
          .select()
          .single();

        if (insertError) throw insertError;
        if (insertData) {
          setSettings({ ...formData, id: insertData.id });
        }
      }

      setSettings(formData);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving loan settings:', err);
      setError(err.message || 'Failed to save loan settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(settings);
    setError(null);
    setIsEditing(false);
  };

  // Superadmin or group admin/clerk can edit loan settings
  const isViewOnly = accessLevel === 'view_only' || (!isSuperAdmin && !isGroupAdmin);

  if (isLoading) {
    return (
      <div className="bg-card rounded-md border border-border shadow-elevation-1 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading loan settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-md border border-border shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="CreditCard" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Loan Settings</h3>
            <p className="text-sm text-muted-foreground">
              Configure loan eligibility and repayment terms
            </p>
          </div>
        </div>
        {!isViewOnly && (
          <Button
            variant={isEditing ? 'outline' : 'default'}
            size="sm"
            onClick={() => (isEditing ? handleCancel() : setIsEditing(true))}
            disabled={isSaving}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Interest Rate (%)"
                type="number"
                value={formData.interest_rate.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    interest_rate: parseFloat(e.target.value) || 0,
                  })
                }
                description="Annual interest rate (0-100%)"
                min="0"
                max="100"
                step="0.1"
                disabled={isSaving || isViewOnly}
              />

              <Input
                label="Minimum Contribution Amount (VT)"
                type="number"
                value={formData.min_contribution_amount.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    min_contribution_amount: parseFloat(e.target.value) || 0,
                  })
                }
                description="Minimum total contributions required to be eligible for a loan"
                min="0"
                step="0.01"
                disabled={isSaving || isViewOnly}
              />

              <Input
                label="Max Loan to Contribution Ratio"
                type="number"
                value={formData.max_loan_to_contribution_ratio.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_loan_to_contribution_ratio: parseFloat(e.target.value) || 0,
                  })
                }
                description="Maximum loan amount as a ratio of total contributions (e.g., 0.5 = 50%)"
                min="0"
                max="1"
                step="0.01"
                disabled={isSaving || isViewOnly}
              />

              <Input
                label="Max Repayment Months"
                type="number"
                value={formData.max_repayment_months.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_repayment_months: parseInt(e.target.value) || 12,
                  })
                }
                description="Maximum number of months for loan repayment"
                min="1"
                step="1"
                disabled={isSaving || isViewOnly}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="default"
                onClick={handleSave}
                disabled={isSaving || isViewOnly}
                loading={isSaving}
              >
                Save Settings
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Percent" size={18} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Interest Rate
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {settings.interest_rate.toFixed(2)}%
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="DollarSign" size={18} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Minimum Contribution
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {settings.min_contribution_amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} VT
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="TrendingUp" size={18} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Max Loan Ratio
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {(settings.max_loan_to_contribution_ratio * 100).toFixed(0)}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  of total contributions
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={18} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Max Repayment Period
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {settings.max_repayment_months} months
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanSettingsCard;

